import { z } from "zod";

/**
 * Legacy preset icon names (kept for backward compatibility)
 */
const presetIconNames = [
  "whatsapp",
  "email",
  "phone",
  "link",
  "messenger",
  "telegram",
  "instagram",
  "x",
  "facebook",
  "linkedin",
  "custom"
] as const;

export const PresetIconNameSchema = z.enum(presetIconNames);
export type PresetIconName = z.infer<typeof PresetIconNameSchema>;

/**
 * Action button icon — discriminated union supporting favicon, emoji, image, and legacy presets
 */
export const ActionButtonIconSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("favicon") }),
  z.object({ type: z.literal("emoji"), emoji: z.string() }),
  z.object({ type: z.literal("image"), url: z.string() }),
  z.object({ type: z.literal("preset"), name: PresetIconNameSchema })
]);

export type ActionButtonIcon = z.infer<typeof ActionButtonIconSchema>;

/**
 * Normalizes legacy string icon values to the new ActionButtonIcon object format.
 */
export function normalizeActionButtonIcon(
  icon: ActionButtonIcon | PresetIconName
): ActionButtonIcon {
  if (typeof icon === "string") {
    return { type: "preset", name: icon };
  }
  return icon;
}

/**
 * Landing screen action button schema (e.g., WhatsApp link, email, phone)
 */
export const LandingScreenActionButtonSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  icon: z.union([ActionButtonIconSchema, PresetIconNameSchema]),
  url: z.string()
});

export type LandingScreenActionButton = z.infer<typeof LandingScreenActionButtonSchema>;

/**
 * Per-language overrides for an action button. The shared base entry on
 * `landingScreenActionButtons` defines the structural fields (id, icon);
 * a localized override may replace any of the user-facing strings (label,
 * description, url) for a specific locale.
 */
export const LandingScreenActionButtonLocalizedOverrideSchema = z.object({
  label: z.string().optional(),
  description: z.string().optional(),
  url: z.string().optional()
});

export type LandingScreenActionButtonLocalizedOverride = z.infer<
  typeof LandingScreenActionButtonLocalizedOverrideSchema
>;

/**
 * Footer link schema (e.g., Terms of Use, Privacy Policy)
 */
export const FooterLinkSchema = z.object({
  id: z.string(),
  label: z.string(),
  url: z.string()
});

export type FooterLink = z.infer<typeof FooterLinkSchema>;

/**
 * Per-language overrides for a footer link. The shared base entry on
 * `footerLinks` defines the structural fields (id); a localized override may
 * replace the user-facing strings (label, url) for a specific locale.
 */
export const FooterLinkLocalizedOverrideSchema = z.object({
  label: z.string().optional(),
  url: z.string().optional()
});

export type FooterLinkLocalizedOverride = z.infer<typeof FooterLinkLocalizedOverrideSchema>;

/**
 * FLAT Embed Settings Schema
 *
 * All config at one level. No nesting. TypeScript is the version control.
 */
export const EmbedSettingsSchema = z.object({
  // Display
  displayName: z.string(),
  /** Localized display name (header title) keyed by locale code */
  localizedDisplayName: z.record(z.string(), z.string()).optional(),
  welcomeMessages: z.array(z.string()),
  /** Localized welcome messages keyed by locale code (e.g. "en", "fr", "es") */
  localizedWelcomeMessages: z.record(z.string(), z.array(z.string())).optional(),
  /** Default language for all localized content (fallback when visitor language is not configured) */
  defaultLanguage: z.string().optional(),
  /**
   * Explicit list of languages the owner has configured in the Content tab.
   * Used to drive the language-tab UI. If omitted, supported languages are
   * derived from the union of keys present in any `localized*` field.
   */
  supportedLanguages: z.array(z.string()).optional(),
  suggestedMessages: z.array(z.string()),
  /** Localized suggested messages keyed by locale code */
  localizedSuggestedMessages: z.record(z.string(), z.array(z.string())).optional(),
  showTimestamp: z.boolean().optional(),

  // Bubble
  bubblePosition: z.enum(["bottom-right", "bottom-left", "top-right", "top-left"]),
  bubbleBorderRadius: z.enum(["none", "sm", "md", "lg", "full"]),
  bubbleBackgroundColor: z.string(),
  bubbleBorderColor: z.string(),
  bubbleBorderWidth: z.number(),
  bubbleSize: z.enum(["small", "medium", "large"]),

  // UI theming
  uiAppearance: z.enum(["system", "light", "dark"]),
  uiRadius: z.enum(["sm", "md", "lg"]),
  uiContainerRadius: z.enum(["sm", "md", "lg", "none"]),
  uiBgPattern: z.string(),
  uiPrimaryColor: z.string(),
  uiAgentMessageColor: z.string(),
  uiAgentMessageTextColor: z.string(),
  uiUserMessageColor: z.string(),
  uiUserMessageTextColor: z.string(),

  // Header
  showHeader: z.boolean(),
  headerMessage: z.string(),
  /** Localized header message keyed by locale code */
  localizedHeaderMessage: z.record(z.string(), z.string()).optional(),

  // Footer
  showFooter: z.boolean(),
  footerMessage: z.string(),
  /** Localized footer messages keyed by locale code */
  localizedFooterMessage: z.record(z.string(), z.string()).optional(),
  footerLinks: z.array(FooterLinkSchema),
  /**
   * Localized overrides for footer link label/url, keyed by locale code then
   * by link id. Falls back to the shared `footerLinks` entry when no override
   * exists. Order, identity (id), and structural inclusion stay shared so
   * adding a link is automatically reflected in every language.
   */
  localizedFooterLinks: z
    .record(z.string(), z.record(z.string(), FooterLinkLocalizedOverrideSchema))
    .optional(),

  // Chat input
  chatInputPlaceholder: z.string(),
  /** Localized chat input placeholder keyed by locale code */
  localizedChatInputPlaceholder: z.record(z.string(), z.string()).optional(),

  // Landing screen (gated by landingScreenEnabled)
  landingScreenEnabled: z.boolean(),
  landingScreenWelcomeMessage: z.string(),
  /** Localized landing screen welcome messages keyed by locale code */
  localizedLandingScreenWelcomeMessage: z.record(z.string(), z.string()).optional(),
  landingScreenNewConversationLabel: z.string(),
  /** Localized "new conversation" button label keyed by locale code */
  localizedLandingScreenNewConversationLabel: z.record(z.string(), z.string()).optional(),
  landingScreenNewConversationDescription: z.string(),
  /** Localized "new conversation" button description keyed by locale code */
  localizedLandingScreenNewConversationDescription: z.record(z.string(), z.string()).optional(),
  landingScreenShowPastConversations: z.boolean(),
  /**
   * When enabled, the visitor can only start a new conversation if there are
   * no past conversations OR the most recent conversation is closed/archived
   * OR its last message is older than `landingScreenInactiveConversationThresholdHours`.
   */
  landingScreenSingleActiveConversation: z.boolean().optional(),
  /**
   * Number of hours after which an existing open conversation is considered
   * inactive enough that the visitor may start a new one. Only consulted when
   * `landingScreenSingleActiveConversation` is true. Defaults to 3.
   */
  landingScreenInactiveConversationThresholdHours: z.number().min(0).optional(),
  /**
   * When enabled, past conversations whose last message is older than
   * `landingScreenHidePastConversationsAfterDays` are hidden from the
   * landing screen list. The effective cutoff is never shorter than
   * `landingScreenInactiveConversationThresholdHours` (when single-active
   * gating is on) so a conversation that still blocks starting a new one
   * can never be hidden — otherwise the visitor would face an empty screen
   * with no way to start a conversation.
   */
  landingScreenHidePastConversationsEnabled: z.boolean().optional(),
  /**
   * Number of days of inactivity after which a past conversation is hidden
   * from the landing screen. Only consulted when
   * `landingScreenHidePastConversationsEnabled` is true. Defaults to 30.
   */
  landingScreenHidePastConversationsAfterDays: z.number().min(0).optional(),
  landingScreenPastConversationsTitle: z.string(),
  /** Localized past conversations section title keyed by locale code */
  localizedLandingScreenPastConversationsTitle: z.record(z.string(), z.string()).optional(),
  landingScreenChannelsTitle: z.string(),
  /** Localized channels section title keyed by locale code */
  localizedLandingScreenChannelsTitle: z.record(z.string(), z.string()).optional(),
  landingScreenActionButtons: z.array(LandingScreenActionButtonSchema),
  /**
   * Localized overrides for landing-screen action buttons, keyed by locale
   * code then by button id. Falls back to the shared
   * `landingScreenActionButtons` entry when no override exists. The icon
   * stays shared across languages.
   */
  localizedLandingScreenActionButtons: z
    .record(z.string(), z.record(z.string(), LandingScreenActionButtonLocalizedOverrideSchema))
    .optional(),
  /** Whether to show a footer message at the bottom of the landing screen */
  landingScreenShowFooter: z.boolean().optional(),
  /** Footer message shown at the bottom of the landing screen */
  landingScreenFooterMessage: z.string().optional(),
  /** Localized landing screen footer message keyed by locale code */
  localizedLandingScreenFooterMessage: z.record(z.string(), z.string()).optional(),
  /** Footer links shown below the footer message on the landing screen */
  landingScreenFooterLinks: z.array(FooterLinkSchema).optional(),
  /**
   * Localized overrides for landing-screen footer link label/url, keyed by
   * locale code then by link id. Falls back to the shared
   * `landingScreenFooterLinks` entry when no override exists.
   */
  localizedLandingScreenFooterLinks: z
    .record(z.string(), z.record(z.string(), FooterLinkLocalizedOverrideSchema))
    .optional(),

  // Identity verification (HMAC signing)
  /** When enabled, externalUserId must be accompanied by a valid HMAC-SHA256 hash */
  identityVerificationEnabled: z.boolean().optional(),
  /** Server-side HMAC secret — never sent to the client, only stored in deployment settings */
  identityVerificationSecret: z.string().min(32).max(64).optional()
});

export type EmbedSettings = z.infer<typeof EmbedSettingsSchema>;

/**
 * How many keys `EmbedSettingsSchema` declares, as a literal a human writes.
 *
 * 🔴 **ADDING OR REMOVING A SETTING ABOVE MEANS EDITING THIS NUMBER IN THE SAME
 * COMMIT.** `embed-settings-key-count-is-declared.test.ts`, beside this file,
 * compares the computed count against it and refuses the diff until they agree.
 *
 * It exists because this count is QUOTED in prose that no compiler and no
 * assertion reaches: the v1 endpoint's docblock states how many keys the widget
 * declares and how many the public config serves, and
 * `content/docs/api-reference/deployments/embed-config.mdx` republishes both
 * figures to customers. Prose cannot be typechecked, so the only way the number
 * stays true is for a change to the schema to fail something.
 *
 * A FLOOR cannot do that job — `expect(count).toBeGreaterThan(50)` is satisfied
 * by any figure above 50, so a stale one and a correct one are the same green.
 * The assertion has to be exact, and the declared side has to be a literal:
 * comparing the computed count to itself passes forever.
 *
 * `EmbedConfigSchema` omits exactly one key from this schema, so the number the
 * public endpoint serves is this one minus one. The spec pins that too.
 */
export const EMBED_SETTINGS_DECLARED_KEY_COUNT = 59;
